// Per-device unread tracking and notifications. Read markers stay on this device.
// Notifications come by push where the browser and server allow it (the app needn't be
// open), else from the open page.
import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'
import type { Uuid } from '../api/types'
import { latestSeq, mergeMarkers, unreadCount } from '../lib/unread'
import { displayName, dmTitle, replyTree, type ReplyTree } from '../lib/format'
import { startMediaWorker } from '../lib/media'
import { clearLabels, keyBytes, nativePush, pushSupported, saveLabels, type PushKeys, type PushLabels } from '../lib/push'
import { isApp, isMobileApp } from '../platform'
import type { StoredMessage } from '../storage/db'
import { useSession } from './session'
import { preview } from '../lib/annotations'

export const useUnread = defineStore('unread', () => {
  const lastRead = ref<Record<string, number>>({})
  /**
   * Threads (by their top-level message) muted on this device: their replies don't notify
   * or count as unread here. Push can't tell: the server doesn't know what a message replies to.
   */
  const mutedThreads = ref<Set<string>>(new Set())
  /** Per thread (its top-level message), the last reply seen in its panel on this device. */
  const threadRead = ref<Record<string, number>>({})
  /** When this device started keeping thread markers: replies before then count as read. */
  const threadsSince = ref(Date.now())
  const active = ref<Uuid | null>(null)
  const notifications = ref(false)
  /** This device gets push notifications, so the page doesn't show its own. */
  const pushing = ref(false)
  /** Why the app can't push (e.g. no UnifiedPush distributor on this phone), for Settings. */
  const pushProblem = ref<string | null>(null)
  /** Nobody has said yet whether this device should notify: NotificationsPrompt asks once (HRTH-200). */
  const notificationsUnasked = ref(false)
  let saveTimer: ReturnType<typeof setTimeout> | null = null

  /** Each conversation's reply tree. */
  const trees = computed(() => {
    const out: Record<string, ReplyTree> = {}
    for (const [conv, msgs] of Object.entries(useSession().messages)) out[conv] = replyTree(msgs)
    return out
  })

  /** Replies in muted threads, in one conversation. */
  function inMutedThreads(conv: string): Set<string> {
    const out = new Set<string>()
    if (!mutedThreads.value.size) return out
    for (const [id, root] of trees.value[conv]?.roots ?? []) if (mutedThreads.value.has(root)) out.add(id)
    return out
  }

  const counts = computed(() => {
    const session = useSession()
    const out: Record<string, number> = {}
    for (const [conv, msgs] of Object.entries(session.messages)) {
      out[conv] = unreadCount(msgs, lastRead.value[conv] ?? 0, session.me, inMutedThreads(conv))
    }
    return out
  })

  /** Replies from others in a thread since I last had it open (none while it's muted). */
  function threadUnread(conv: string, root: string): number {
    const session = useSession()
    if (mutedThreads.value.has(root)) return 0
    const roots = trees.value[conv]?.roots
    const read = threadRead.value[root] ?? 0
    let n = 0
    for (const m of session.messages[conv] ?? []) {
      if (m.seq > read && roots?.get(m.envelope.id) === root && m.sender.user_id !== session.me && (m.envelope.ts || m.receivedAt) > threadsSince.value) n++
    }
    return n
  }

  /** The thread is on screen: everything in it up to `seq` is seen. */
  function markThreadRead(root: string, seq: number): void {
    if (document.hidden || seq <= (threadRead.value[root] ?? 0)) return
    threadRead.value = { ...threadRead.value, [root]: seq }
    void useSession().storageHandle()?.setKv('threadRead', { ...threadRead.value })
  }

  async function muteThread(root: string, on: boolean): Promise<void> {
    const next = new Set(mutedThreads.value)
    if (on) next.add(root)
    else next.delete(root)
    mutedThreads.value = next
    await useSession().storageHandle()?.setKv('mutedThreads', [...next])
  }

  const total = computed(() => Object.values(counts.value).reduce((a, b) => a + b, 0))

  function spaceUnread(channelIds: string[]): number {
    return channelIds.reduce((n, id) => n + (counts.value[id] ?? 0), 0)
  }

  async function load(): Promise<void> {
    const session = useSession()
    const storage = session.storageHandle()
    // Merge rather than replace: markers may have advanced while this was loading.
    lastRead.value = mergeMarkers((await storage?.getKv<Record<string, number>>('lastRead')) ?? {}, lastRead.value)
    mutedThreads.value = new Set((await storage?.getKv<string[]>('mutedThreads')) ?? [])
    threadRead.value = mergeMarkers((await storage?.getKv<Record<string, number>>('threadRead')) ?? {}, threadRead.value)
    const since = await storage?.getKv<number>('threadsSince')
    if (since) threadsSince.value = since
    else await storage?.setKv('threadsSince', threadsSince.value)
    // The phone apps' WebView never grants the web permission: their shell asks the OS instead.
    const granted = isMobileApp || (typeof Notification !== 'undefined' && Notification.permission === 'granted')
    const chosen = await storage?.getKv<boolean>('notifications')
    notifications.value = (chosen ?? false) && granted
    notificationsUnasked.value = chosen == null && !(await storage?.getKv<boolean>('notificationsAsked'))
  }

  function persist(): void {
    if (saveTimer) clearTimeout(saveTimer)
    saveTimer = setTimeout(() => void useSession().storageHandle()?.setKv('lastRead', { ...lastRead.value }), 300)
  }

  /** The conversation on screen; it's read while visible. */
  function setActive(conv: Uuid | null): void {
    active.value = conv
    markRead()
  }

  function markRead(): void {
    const conv = active.value
    if (!conv || document.hidden) return
    const latest = latestSeq(useSession().messages[conv] ?? [])
    if (globalThis.localStorage?.getItem('hrthDebug') === '1') console.debug('[unread] markRead', conv.slice(-4), latest, location.pathname.slice(-4))
    if (latest > (lastRead.value[conv] ?? 0)) {
      lastRead.value = { ...lastRead.value, [conv]: latest }
      persist()
    }
  }

  async function enableNotifications(on: boolean): Promise<void> {
    // The phone apps ask the OS while subscribing (nativePush.subscribe): their WebView's
    // Notification.requestPermission never settles.
    if (on && !isMobileApp) {
      if (typeof Notification === 'undefined') on = false
      else if (Notification.permission !== 'granted') on = (await Notification.requestPermission()) === 'granted'
    }
    notifications.value = on
    notificationsUnasked.value = false
    if (on) await syncPush()
    else await stopPush()
    if (on && isMobileApp && !pushing.value) {
      const allowed = (await nativePush.status().catch(() => null))?.notifications
      if (allowed && allowed !== 'granted') notifications.value = false
    }
    await useSession().storageHandle()?.setKv('notifications', notifications.value)
  }

  /** "Not now" to NotificationsPrompt: it doesn't ask again on this device. */
  async function declineNotifications(): Promise<void> {
    notificationsUnasked.value = false
    await useSession().storageHandle()?.setKv('notificationsAsked', true)
  }

  async function registration(): Promise<ServiceWorkerRegistration | null> {
    if (!('serviceWorker' in navigator)) return null
    await startMediaWorker()
    return (await navigator.serviceWorker.getRegistration('/')) ?? null
  }

  /** Subscribes this device (again) for push; false where push isn't available. */
  async function subscribePush(): Promise<boolean> {
    const session = useSession()
    const key = session.features.push_key
    const e = session.engine
    if (!key || !e) return false
    if (isApp) return subscribeNative(key, e.api)
    if (!pushSupported()) return false
    try {
      const reg = await registration()
      if (!reg) return false
      let sub = await reg.pushManager.getSubscription()
      // One made with another key (the server's changed) can't be used.
      const want = keyBytes(key)
      const had = sub?.options.applicationServerKey
      if (sub && !(had && new Uint8Array(had).every((b, i) => b === want[i]) && had.byteLength === want.length)) {
        await sub.unsubscribe()
        sub = null
      }
      sub ??= await Promise.race([
        reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: want }),
        new Promise<never>((_, reject) => setTimeout(() => reject(new Error('push service timed out')), 15_000)),
      ])
      const { keys } = sub.toJSON()
      await e.api.pushSubscribe({ endpoint: sub.endpoint, p256dh: keys?.p256dh ?? '', auth: keys?.auth ?? '' })
      return true
    } catch (err) {
      console.info('[hrth] push notifications unavailable; notifying from the open app', err)
      return false
    }
  }

  async function subscribeNative(key: string, api: { pushSubscribe: (s: PushKeys) => Promise<unknown> }): Promise<boolean> {
    try {
      if ((await nativePush.status()).platform === 'none') return false
      await api.pushSubscribe(await nativePush.subscribe(key))
      pushProblem.value = null
      return true
    } catch (err) {
      pushProblem.value = err instanceof Error ? err.message : String(err)
      console.info('[hrth] push notifications unavailable; notifying from the open app', err)
      return false
    }
  }

  /** Brings this device's push subscription in line with the setting and the server's. */
  async function syncPush(): Promise<void> {
    if (!notifications.value) return
    pushing.value = await subscribePush()
    if (pushing.value) await writeLabels()
  }

  /** Stops pushes to this device (turned off, or signing out). */
  async function stopPush(): Promise<void> {
    pushing.value = false
    pushProblem.value = null
    if (isApp) {
      await nativePush.unsubscribe().catch(() => undefined)
      await useSession()
        .engine?.api.pushUnsubscribe()
        .catch(() => undefined)
      return
    }
    await clearLabels()
    const sub = await navigator.serviceWorker
      ?.getRegistration('/')
      .then((r) => r?.pushManager?.getSubscription())
      .catch(() => null)
    await sub?.unsubscribe().catch(() => undefined)
    await useSession()
      .engine?.api.pushUnsubscribe()
      .catch(() => undefined)
  }

  /** Names the Service Worker uses for pushes: conversations and people this device knows. */
  function labels(): PushLabels {
    const s = useSession()
    const convs: PushLabels['convs'] = {}
    for (const sp of s.spaces) {
      for (const c of sp.channels) convs[c.id] = { title: `${sp.name} › #${c.name ?? ''}`, url: `/s/${sp.id}/${c.id}` }
    }
    for (const c of s.dms) {
      convs[c.id] = { title: dmTitle(s.profiles, c.members, s.me, c.name), url: `/dm/${c.id}`, direct: !c.group }
    }
    const users = Object.fromEntries([...s.profiles.values()].map((p) => [p.user_id, p.display_name]))
    return { app: s.serverName, convs, users }
  }

  let lastLabels = ''
  async function writeLabels(): Promise<void> {
    if (isApp) {
      // Sync refreshes rebuild these objects often; the shell writes them to storage.
      const now = labels()
      const json = JSON.stringify(now)
      if (json === lastLabels) return
      lastLabels = json
      await nativePush.setLabels(now).catch(() => (lastLabels = ''))
    } else {
      await saveLabels(labels()).catch(() => undefined)
    }
  }

  let labelTimer: ReturnType<typeof setTimeout> | null = null
  watch(
    () => {
      const s = useSession()
      return [s.spaces, s.dms, s.profiles, s.serverName] as const
    },
    () => {
      if (!pushing.value) return
      if (labelTimer) clearTimeout(labelTimer)
      labelTimer = setTimeout(() => void writeLabels(), 500)
    },
  )

  // The installed app's icon shows how many messages are unread.
  watch(total, (n) => {
    const nav = navigator as Navigator & { setAppBadge?: (n?: number) => Promise<void>; clearAppBadge?: () => Promise<void> }
    void (n ? nav.setAppBadge?.(n) : nav.clearAppBadge?.())?.catch(() => undefined)
  })

  /** Called for every batch of new messages. */
  function onMessages(conv: Uuid, msgs: StoredMessage[]): void {
    if (conv === active.value && !document.hidden) {
      markRead()
      return
    }
    const session = useSession()
    if (!notifications.value || pushing.value || typeof Notification === 'undefined') return
    const muted = session.muted.has(conv)
    const quiet = inMutedThreads(conv)
    for (const m of msgs) {
      if (m.sender.user_id === session.me) continue
      const e = m.envelope
      // A muted conversation or thread still tells me when someone mentions me.
      const mentioned = e.type === 'text' && !!session.me && (e.mentions ?? []).includes(session.me)
      if ((muted || quiet.has(e.id)) && !mentioned) continue
      const text = e.type === 'text' ? e.body : e.type === 'media' || e.type === 'library' ? preview(m, undefined) : null
      if (!text) continue
      const c = session.conversations.get(conv)
      const where = c?.kind === 'channel' ? ` in #${c.name}` : ''
      show(`${displayName(session.profiles, m.sender.user_id)}${mentioned ? ' mentioned you' : ''}${where}`, text, conv)
    }
  }

  /** Android won't construct notifications in a page: they go through the Service Worker there. */
  function show(title: string, body: string, conv: Uuid): void {
    try {
      new Notification(title, { body, tag: conv })
    } catch {
      void registration().then((r) => r?.showNotification(title, { body, tag: conv, icon: '/icon-192.png' }))
    }
  }

  return {
    lastRead,
    counts,
    trees,
    mutedThreads,
    muteThread,
    threadUnread,
    markThreadRead,
    total,
    spaceUnread,
    load,
    setActive,
    markRead,
    declineNotifications,
    enableNotifications,
    notifications,
    notificationsUnasked,
    pushing,
    pushProblem,
    syncPush,
    stopPush,
    onMessages,
  }
})
